/* begin general import */
import Close16 from "@carbon/icons-react/es/close/16";
import Send16 from "@carbon/icons-react/es/send/16";
import { Col, Row, Switch } from "antd";
import { LOGIN_CONFIGURATION_ROUTE } from "config/route-consts";
import { AppUser } from "models/AppUser";
import { LoginConfiguration } from "models/LoginConfiguration";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, StandardTable } from "react3l-ui-library";
import FormItem from "react3l-ui-library/build/components/FormItem";
import InputText from "react3l-ui-library/build/components/Input/InputText";
import { loginConfigurationRepository } from "repositories/login-configuration-repository";
import { utilService } from "services/common-services/util-service";
import { detailService } from "services/page-services/detail-service";
import { fieldService } from "services/page-services/field-service";
import nameof from "ts-nameof.macro";
import { useLoginConfigurationContent } from "./LoginConfigurationContentHook";
import "./LoginConfigurationDetail.scss";
import { authorizationService } from "services/common-services/authorization-service";
import { API_LOGIN_CONFIGURATION_PREFIX } from "config/api-consts";

function LoginConfigurationDetail() {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    LOGIN_CONFIGURATION_ROUTE.split("/")[
      LOGIN_CONFIGURATION_ROUTE.split("/").length - 1
    ],
    API_LOGIN_CONFIGURATION_PREFIX
  );

  const { model, dispatch } =
    detailService.useModel<LoginConfiguration>(LoginConfiguration);

  const { isDetail } = detailService.useGetIsDetail<LoginConfiguration>(
    loginConfigurationRepository.get,
    dispatch
  );

  const { columns, list } = useLoginConfigurationContent(model, dispatch);

  const { handleChangeAllField, handleChangeSingleField } =
    fieldService.useField(model, dispatch);
  const ref = React.useRef<boolean>(true);
  React.useEffect(() => {
    if (ref.current && !model?.id) {
      handleChangeAllField({ ...model, statusId: 1 });
      ref.current = false;
    }
  }, [model, handleChangeAllField]);

  const { handleSaveModel, handleGoMaster } =
    detailService.useActionsDetail<AppUser>(
      model,
      loginConfigurationRepository.save,
      handleChangeAllField,
      LOGIN_CONFIGURATION_ROUTE
    );
  const handleChangeStatus = React.useCallback(
    (checked) => {
      const newModel = { ...model };
      if (checked) {
        newModel.statusId = 1;
      } else {
        newModel.statusId = 0;
      }
      handleChangeAllField(newModel);
    },
    [handleChangeAllField, model]
  );
  return (
    <>
      <div className="page-content page-content-dxg" id="page-content-dxg-id">
        <div className="info-component ml-4 mb-3">
          <span>{translate("loginConfigurations.master.header")}</span>
          <span> &gt; </span>
          <span className="info-primary">
            {translate("loginConfigurations.master.subHeader")}
          </span>
        </div>

        <div className={`page page-detail p-t--lg p-l--md p-r--sm p-b--lg `}>
          <div className="mb-0 d-flex justify-content-between background-w-dxg border-tl-8-dxg">
            <h5 className="font-bold page-title">
              {translate("general.detail.title")}
            </h5>
            <div className="mr-3">
              <Button
                type="secondary"
                className="btn--lg btn-red"
                icon={<Close16 />}
                onClick={handleGoMaster}
              >
                {translate("general.actions.close")}
              </Button>
              {((!isDetail && validAction("create")) ||
                (isDetail && validAction("update"))) && (
                <Button
                  type="primary"
                  className="btn--lg btn-orange-wIcon ml-3"
                  icon={<Send16 />}
                  onClick={handleSaveModel}
                >
                  {translate("general.actions.save")}
                </Button>
              )}
            </div>
          </div>

          <div className="page-detail__title p-b--sm">
            {!isDetail
              ? translate("general.actions.create")
              : translate("general.detail.title")}
          </div>
          <Row className="m-t--xxs m-l--xxxs">
            <Col span={6} className="m-b--xxs p-r--sm">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  model,
                  nameof(model.code)
                )}
                message={model.errors?.code}
              >
                <InputText
                  label={translate("loginConfigurations.code")}
                  type={0}
                  isRequired
                  value={model.code}
                  placeHolder={translate(
                    "loginConfigurations.placeholder.code"
                  )}
                  className={"tio-account_square_outlined"}
                  onChange={handleChangeSingleField({
                    fieldName: nameof(model.code),
                  })}
                />
              </FormItem>
            </Col>
            <Col span={6} className="m-b--xxs p-r--sm">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  model,
                  nameof(model.name)
                )}
                message={model.errors?.name}
              >
                <InputText
                  label={translate("loginConfigurations.name")}
                  type={0}
                  isRequired
                  value={model.name}
                  placeHolder={translate(
                    "loginConfigurations.placeholder.name"
                  )}
                  className={"tio-account_square_outlined"}
                  onChange={handleChangeSingleField({
                    fieldName: nameof(model.name),
                  })}
                />
              </FormItem>
            </Col>

            <Col span={6} className="m-b--xxs p-r--sm">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  model,
                  nameof(model.status)
                )}
                message={model.errors?.status}
              >
                <div>
                  <div className="label-status">
                    {translate("loginConfigurations.status")}
                  </div>
                  <Switch
                    checked={model.statusId === 1 ? true : false}
                    onChange={handleChangeStatus}
                    className="switch_status"
                  />
                </div>
              </FormItem>
            </Col>
          </Row>
          <div className="page-detail__title m-b--xxl m-t--xl">
            Cấu hình chi tiết
          </div>
          <Row>
            <div className="table-content">
              <StandardTable
                rowKey={nameof(list[0].key)}
                columns={columns}
                dataSource={list}
                tableSize={"lg"}
              />
            </div>
          </Row>
        </div>
      </div>
    </>
  );
}

export default LoginConfigurationDetail;
